import {useEffect, useRef, useState, type PropsWithChildren, type ReactNode} from 'react'
import {
  getCustomInstructionsFromReferences,
  getCustomInstructionsFromReferencesAsync,
  isFileReference,
  makeRepositoryReference,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import type {CopilotChatReference, CopilotChatRepo, CopilotCustomInstructions} from '@github-ui/copilot-chat-types'
import {
  filterContributedInstructions,
  type InstructionScope,
} from '@github-ui/ahp-session-client/contributed-instructions'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {
  ContributedInstructionsProvider,
  PersonalInstructionsEditorProvider,
  type ContributedInstructionSnapshot,
  useContributedInstructionSnapshot as useSharedContributedInstructionSnapshot,
  useHasContributedInstructionsProvider,
  useOptionalContributedInstructionSnapshot as useSharedOptionalContributedInstructionSnapshot,
} from '@github-ui/copilot-shared-ui/contexts/ContributedInstructionsContext'

import {InstructionRulesPublisher, type InstructionRulesState} from './instruction-rules-publisher'

const EMPTY_REFERENCES: CopilotChatReference[] = []
const EMPTY_INSTRUCTIONS: ContributedInstructionSnapshot = []

export interface CustomInstructionsProviderProps {
  personalInstructions?: string | null
  customInstructions?: CopilotCustomInstructions
  references?: CopilotChatReference[]
  /** Repository attachments are context; only repository-backed sessions use repository instructions. */
  instructionScope?: InstructionScope
  /** Initial configuration has not arrived yet. Keep this false after the first publication. */
  configurationPending?: boolean
  waitForInitialInstructionRules?: boolean
  loadingFallback?: ReactNode
  onInstructionRulesPublished?: () => void
}

async function fetchRepoForInstructions(repoID: number) {
  const response = await reactFetchJSON(`/github-copilot/chat/repositories/${repoID}`)
  if (!response.ok) {
    // eslint-disable-next-line no-console
    console.error(`[copilot-chat-app] failed to fetch repository ${repoID} for custom instructions`)
    return null
  }
  return makeRepositoryReference((await response.json()) as CopilotChatRepo)
}

function resolveReferenceInstructions(
  references: CopilotChatReference[],
): CopilotCustomInstructions[] | Promise<CopilotCustomInstructions[]> {
  return references.some(isFileReference)
    ? getCustomInstructionsFromReferencesAsync(references, fetchRepoForInstructions)
    : getCustomInstructionsFromReferences(references)
}

export function CustomInstructionsProvider({
  personalInstructions = null,
  customInstructions,
  references = EMPTY_REFERENCES,
  instructionScope = 'chat',
  configurationPending = false,
  waitForInitialInstructionRules = false,
  loadingFallback = null,
  onInstructionRulesPublished,
  children,
}: PropsWithChildren<CustomInstructionsProviderProps>) {
  const shouldWait = waitForInitialInstructionRules
  const readyRef = useRef(false)
  const hasPublishedRef = useRef(false)
  const [ready, setReady] = useState(false)
  const [error, setError] = useState<Error>()
  const [snapshot, setSnapshot] = useState(EMPTY_INSTRUCTIONS)
  const [lastPersonalInstructionsProp, setLastPersonalInstructionsProp] = useState(personalInstructions)
  const [currentPersonalInstructions, setCurrentPersonalInstructions] = useState(personalInstructions)
  const [publisher] = useState(
    () => new InstructionRulesPublisher(resolveReferenceInstructions, undefined, setSnapshot),
  )
  const onInstructionRulesPublishedRef = useRef(onInstructionRulesPublished)

  if (personalInstructions !== lastPersonalInstructionsProp) {
    setLastPersonalInstructionsProp(personalInstructions)
    setCurrentPersonalInstructions(personalInstructions)
  }

  useEffect(() => {
    if (configurationPending) return
    hasPublishedRef.current = false
    let cancelled = false
    const state: InstructionRulesState = {
      personalInstructions: currentPersonalInstructions,
      customInstructions,
      references: copilotLocalStorage.getRepoCustomInstructionsState() ? references : EMPTY_REFERENCES,
      instructionScope,
    }

    void (async () => {
      try {
        await publisher.update(state)
        if (!cancelled) {
          hasPublishedRef.current = true
          readyRef.current = true
          setReady(true)
          onInstructionRulesPublishedRef.current?.()
        }
      } catch (caughtError) {
        if (cancelled) return
        const publicationError =
          caughtError instanceof Error
            ? caughtError
            : new Error('Failed to publish Copilot custom instructions.', {cause: caughtError})
        if (!readyRef.current) {
          setError(publicationError)
        } else {
          // eslint-disable-next-line no-console
          console.error('[copilot-chat-app] failed to publish custom instructions', publicationError)
        }
      }
    })()

    return () => {
      cancelled = true
    }
  }, [
    configurationPending,
    currentPersonalInstructions,
    customInstructions,
    instructionScope,
    publisher,
    references,
    shouldWait,
  ])

  useEffect(() => {
    onInstructionRulesPublishedRef.current = onInstructionRulesPublished
    if (hasPublishedRef.current) onInstructionRulesPublished?.()
  }, [onInstructionRulesPublished])

  useEffect(() => {
    return () => publisher.clear()
  }, [publisher])

  if (error) throw error

  return (
    <PersonalInstructionsEditorProvider
      personalInstructions={currentPersonalInstructions}
      onPersonalInstructionsChange={setCurrentPersonalInstructions}
    >
      <ContributedInstructionsProvider
        instructions={filterContributedInstructions(snapshot, instructionScope)}
        ready={!configurationPending && ready}
      >
        {!shouldWait || (!configurationPending && ready) ? children : loadingFallback}
      </ContributedInstructionsProvider>
    </PersonalInstructionsEditorProvider>
  )
}

export function useHasCustomInstructionsProvider(): boolean {
  return useHasContributedInstructionsProvider()
}

export function useContributedInstructionSnapshot(): ContributedInstructionSnapshot {
  return useSharedContributedInstructionSnapshot()
}

export function useOptionalContributedInstructionSnapshot(): ContributedInstructionSnapshot {
  return useSharedOptionalContributedInstructionSnapshot()
}
